import { useNavigate } from 'react-router-dom';
import type { VideoDetail } from '@streamr/shared';
import { Button } from './ui';
import { useReaction, useSubscription } from '../api/hooks/useEngagement';
import { useAuth } from '../auth/AuthProvider';
import { cn, formatCount } from '../lib/format';

/**
 * Like / dislike. The active state comes from `viewerReaction` on the video
 * payload -- rendering it from local click state instead would show an
 * un-highlighted button to someone who already liked the video on another
 * device, which reads as a bug and cannot be fixed client-side.
 */
export const ReactionButtons = ({ video }: { video: VideoDetail }): JSX.Element => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toggle, pending } = useReaction(video.id);
  const current = video.viewerReaction ?? null;

  const act = (kind: 'LIKE' | 'DISLIKE'): void => {
    if (user === null) {
      navigate('/login', { state: { from: `/watch/${video.id}` } });
      return;
    }
    toggle(kind, current);
  };

  return (
    <div className="flex items-center overflow-hidden rounded-full bg-ink-600">
      <button
        type="button"
        onClick={() => act('LIKE')}
        disabled={pending}
        aria-pressed={current === 'LIKE'}
        aria-label={`Like (${video.likeCount})`}
        className={cn(
          'flex items-center gap-2 px-4 py-2 text-sm hover:bg-ink-500 disabled:opacity-60',
          current === 'LIKE' ? 'text-brand-400' : 'text-ink-100',
        )}
      >
        <span aria-hidden="true">&#9650;</span>
        {formatCount(video.likeCount)}
      </button>
      <span className="h-5 w-px bg-ink-500" />
      <button
        type="button"
        onClick={() => act('DISLIKE')}
        disabled={pending}
        aria-pressed={current === 'DISLIKE'}
        aria-label={`Dislike (${video.dislikeCount})`}
        className={cn(
          'flex items-center gap-2 px-4 py-2 text-sm hover:bg-ink-500 disabled:opacity-60',
          current === 'DISLIKE' ? 'text-brand-400' : 'text-ink-100',
        )}
      >
        <span aria-hidden="true">&#9660;</span>
        {formatCount(video.dislikeCount)}
      </button>
    </div>
  );
};

export const SubscribeButton = ({
  channelId,
  handle,
  isSubscribed,
  isOwner,
}: {
  channelId: string;
  handle?: string;
  isSubscribed: boolean;
  isOwner: boolean;
}): JSX.Element | null => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toggle, pending } = useSubscription(channelId, handle);

  // Subscribing to yourself is not a state worth supporting.
  if (isOwner) return null;

  return (
    <Button
      variant={isSubscribed ? 'secondary' : 'primary'}
      disabled={pending}
      onClick={() => {
        if (user === null) {
          navigate('/login');
          return;
        }
        toggle(isSubscribed);
      }}
    >
      {isSubscribed ? 'Subscribed' : 'Subscribe'}
    </Button>
  );
};
